import { useState, useCallback, memo, useRef } from 'react';
import { useTracking } from 'react-tracking';
import { Heading, Box } from '@simplywallst/ui-core';
import FastCommentsWidget, {
  useFastCommentsCount,
} from '@/components/FastCommentWidget';
import { Sentry } from '@/components/Sentry';
import useUser from '@/hooks/useUser';
import loadable from '@loadable/component';
import If from '@/components/If';
import { usePersistentStorage } from '@/hooks/useCoupon';
import { useFlag } from '@/features/unleash';

import GuidelinesNotice from './GuidelinesNotice';
import CommentCount from './CommentCount';
import { COMMUNITY_GUIDELINES_ACCEPTED } from './constants';

const GuestRegistrationModal = loadable(
  () => import('@components/GuestRegistrationModal'),
  {
    ssr: false,
  }
);

const CommunityGuidelinesModal = loadable(
  () => import('./CommunityGuidelines'),
  {
    ssr: false,
  }
);

const FastComments = memo(FastCommentsWidget);

interface Props {
  canonicalUrl: string;
}

const NarrativeComment = ({ canonicalUrl }: Props) => {
  const { trackEvent } = useTracking();
  const { enabled, flagsReady } = useFlag({ name: 'mono-narrative-comment' });
  const { setCommentCount } = useFastCommentsCount(canonicalUrl);
  const { set, get } = usePersistentStorage();
  const continueSubmitFnRef = useRef<() => void>();
  const cancelFnRef = useRef<() => void>();
  const [state, setState] = useState<
    'idle' | 'registration' | 'community_guidelines'
  >('idle');

  const confirmationCallback = useCallback(
    (status) => {
      setState('idle');
      if (status) {
        set(COMMUNITY_GUIDELINES_ACCEPTED, 'true');
        if (typeof continueSubmitFnRef.current === 'function') {
          trackEvent({
            modifier: 'createComment',
            action: 'click',
          });
          continueSubmitFnRef.current();
        }
        return;
      }
      if (typeof cancelFnRef.current === 'function') {
        cancelFnRef.current();
      }
    },
    [set]
  );

  const onCommentSubmitStart = useCallback(
    async (_, continueSubmitFn, cancelFn) => {
      const isAccepted = await get(COMMUNITY_GUIDELINES_ACCEPTED);
      if (isAccepted === 'true') {
        trackEvent({
          modifier: 'createComment',
          action: 'click',
        });
        continueSubmitFn();
        return;
      }
      setState('community_guidelines');
      continueSubmitFnRef.current = continueSubmitFn;
      cancelFnRef.current = cancelFn;
    },
    [get]
  );

  const openRegistration = useCallback(() => {
    setState('registration');
  }, []);

  const { data: userData } = useUser();
  if (!enabled || !flagsReady) {
    return null;
  }
  return (
    <Sentry
      sentryCapture={{ team: 'narwhals', extra: { canonicalUrl } }}
      globalDisabled
    >
      <If match={state === 'community_guidelines'}>
        {() => (
          <CommunityGuidelinesModal
            onClose={() => confirmationCallback(false)}
            onDecline={() => confirmationCallback(false)}
            onAccept={() => confirmationCallback(true)}
          />
        )}
      </If>
      <If match={state === 'registration'}>
        {() => (
          <GuestRegistrationModal
            canClose={true}
            onClose={() => {
              setState('idle');
            }}
            onRegisterSuccess={() => {
              setState('idle');
            }}
            onLoginSuccess={() => {
              setState('idle');
            }}
          />
        )}
      </If>
      <Box
        paddingY="x2"
        paddingX="x3"
        borderRadius="x0"
        borderRadiusMd="x1"
        backgroundColor="noir"
      >
        <Heading as="h2" typography="small" color="whiteRGBA" opacity={0.5}>
          Comments <CommentCount canonicalUrl={canonicalUrl} />
        </Heading>
        <GuidelinesNotice
          openCommunityGuidelines={() => setState('community_guidelines')}
        />
        <FastComments
          setShowRegModal={openRegistration}
          onCommentSubmitStart={onCommentSubmitStart}
          commentCountUpdated={setCommentCount}
          canonicalURL={canonicalUrl}
          userData={userData}
        />
      </Box>
    </Sentry>
  );
};

export default NarrativeComment;
